import { useAppDispatch, useAppSelector } from 'hooks/redux';
// CORE
import useKickoffA2AFlow from 'hooks/useKickoffA2AFlow';
import type { List as ImList, Set } from 'immutable';
import { Map as ImmutableMap } from 'immutable';
import React, { useState, useMemo, useCallback } from 'react';
import { useToggle } from 'react-use';

import ManageHistoryIcon from '@mui/icons-material/ManageHistory';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { makeStyles } from 'tss-react/mui';

// MUI ICONS
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import ExploreIcon from '@mui/icons-material/Explore';
import ExploreOffIcon from '@mui/icons-material/ExploreOff';
import LinkIcon from '@mui/icons-material/Link';
import NotInterestedRoundedIcon from '@mui/icons-material/NotInterestedRounded';
import RefreshIcon from '@mui/icons-material/Refresh';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';

import { a2aTransfersSelectors } from 'app-components/flux/a2aTransfers';
import {
  accountDiscoveryActions,
  aggErrors,
  aggErrorsSelectors,
  fiMigrationUtils,
} from 'app-components/flux/account-discovery';
// DATA
import type { accountsTypes } from 'app-components/flux/accounts';
import { actionHelpers } from 'app-components/flux/core';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import {
  institutionLoginsActions,
  institutionLoginsSelectors,
  institutionLoginsTypes,
  institutionLoginsUtils,
} from 'app-components/flux/institution-logins';

import { rootUiActions, rootUiTypes } from 'app-components/flux/root-ui';
// UTILS
import { tracker } from 'app-components/utils/core';

// LOCAL
import AccountListItem from 'components/AccountCard/AccountListItem';
import AccountManageUpdatesChip from 'components/AccountManageUpdatesChip';
import AccountRefreshChip from 'components/AccountRefreshChip';
import { onboardA2ADialogIndexEnums } from 'components/Dialogs/A2A/onboardA2ADialogIndexEnums';
import { showRealEstateDialog, showVehicleDialog, VehicleDialogOrigin } from 'components/Dialogs/AssetDialog/actions';
import {
  DIALOG_TYPE as CONFIRMATION_DIALOG_TYPE,
  mkConfirmationDialogProps,
} from 'components/Dialogs/ConfirmationDialog';
import showManageUpdatesDialog from 'components/Dialogs/ManageUpdatesDialog/dispatcher';
import StdDialog from 'components/Dialogs/StdDialog';
import Dump from 'components/Dump';
import { AppleWalletErrorBanner, InstitutionLoginStatusBanner } from 'components/FiStatusBanner';
import InstitutionIcon from 'components/InstitutionIcon';
import InstitutionItem from 'components/InstitutionItem';
import { QDropdownMenu, QMenuDivider, QMenuItem } from 'components/QDropdowns';

import InstitutionErrorBanner from './InstitutionErrorBanner';
import AccountConnectionUpdateBanner from 'components/FiMigration/AccountConnectionUpdateBanner';
import ConnectionUpdateIcon from 'components/FiMigration/ConnectionUpdateIcon';
import useConnectionUpdate from 'components/FiMigration/useConnectionUpdate';
import { useFiMigrationPrompt } from 'app-components/hooks/accounts/useFiMigration';
import Chip from '@mui/material/Chip';
import { hasConnectedAccounts } from './selectors';
import styles from './styles';

// @ts-expect-error FIXME TS2345
const useStyles = makeStyles()(styles);

type Props = {
  institutionLogin?: institutionLoginsTypes.InstitutionLogin; // Note - undefined for manual accounts card
  id: string;
  title?: string;
  accounts: ImList<accountsTypes.Account> | null;
};

const AccountCard = (props: Props) => {
  const { institutionLogin, id, title, accounts } = props;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const connectionUpdate = useConnectionUpdate(institutionLogin?.id, {
    entry: 'settings overflow',
    location: 'account settings',
  });
  const trackMigrationPrompt = useFiMigrationPrompt({
    institutionLogin: connectionUpdate.institutionLogin,
    surface: 'settings overflow',
    location: 'account settings',
    visible: isMenuOpen,
  });

  const dispatch = useAppDispatch();
  const { classes } = useStyles();

  const [showExpandedMenu, setShowExpandedMenu] = useState(false);
  const menuId = `${id}-menu`;
  const [updatedInstitutionName, setUpdatedInstitutionName] = useState<string | null>(null);
  const [showEditDialog, toggleShowEditDialog] = useToggle(false);

  const aggLoading = useAppSelector((state) =>
    institutionLogin
      ? (institutionLoginsSelectors.getIdsRefreshing(state) as Set<string>)?.includes(institutionLogin.id)
      : false,
  );
  const aggAction = useAppSelector((state) =>
    aggErrorsSelectors.getAggregationAction(state, { institutionLoginId: institutionLogin?.id ?? '' }),
  );

  const a2aTransferEnabled = useAppSelector(a2aTransfersSelectors.a2aTransferEnabled);
  const institution = useAppSelector((state) =>
    institutionLoginsSelectors.getInstitutionForInstitutionLogin(state, institutionLogin?.id),
  );
  const loginHasConnectedAccountsProps = useMemo(
    () => ({ institutionLoginId: institutionLogin?.id || null }),
    [institutionLogin],
  );
  const loginHasConnectedAccounts = useAppSelector((state) =>
    // @ts-expect-error FIXME TS2345
    hasConnectedAccounts(state, loginHasConnectedAccountsProps),
  );
  const fiStatusEnabled = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'fiStatus'));

  const isReportableError =
    aggAction &&
    !fiMigrationUtils.isFiMigrationAction(aggAction) &&
    institutionLoginsUtils.isErrorType(aggAction?.status);
  const isZillow = institutionLogin?.channel === 'ZILLOW';
  const isKbb = institutionLogin?.channel === 'KELLEY_BLUE_BOOK';
  const kickoffA2AFlow = useKickoffA2AFlow();

  const isRefreshFrequencySupported = useAppSelector((state) =>
    institutionLoginsSelectors.isRefreshFrequencySupported(state, institutionLogin?.id || ''),
  );

  const handleUpdateInstitutionName = useCallback(() => {
    toggleShowEditDialog();

    if (institutionLogin) {
      dispatch(
        institutionLoginsActions.updateInstitutionLogin(
          institutionLoginsTypes.mkInstitutionLogin({
            id: institutionLogin.id,
            institutionId: institutionLogin.institutionId,
            name: updatedInstitutionName as string,
          }),
        ),
      );
      tracker.track(tracker.events.fiLoginNameSave, { institution_id: institutionLogin?.institutionId || '0' });
    }
  }, [institutionLogin, dispatch, updatedInstitutionName, toggleShowEditDialog]);

  const deleteAccount = useCallback(() => {
    actionHelpers
      .bindPromiseAction(
        dispatch,
        institutionLoginsActions.deleteInstitutionLogin,
      )(institutionLogin)
      .then(() => {
        dispatch(
          rootUiActions.createDialog(
            rootUiTypes.mkRootUiData({
              id: 'fi-deletion-confirmation-dialog',
              type: CONFIRMATION_DIALOG_TYPE,
              props: ImmutableMap(
                mkConfirmationDialogProps({
                  title: 'Delete Financial Institution Success',
                  content: (
                    <Typography variant='titleRegular'>
                      {' '}
                      Your financial institution has been successfully deleted.
                    </Typography>
                  ),
                }),
              ),
            }),
          ),
        );
      })
      .catch((error) => {
        dispatch(
          rootUiActions.createDialog(
            rootUiTypes.mkRootUiData({
              id: 'fi-deletion-failure-dialog',
              type: CONFIRMATION_DIALOG_TYPE,
              props: ImmutableMap(
                mkConfirmationDialogProps({
                  title: 'Financial Institution Deletion Failure',
                  content: (
                    // @ts-expect-error FIXME TS2339
                    <Typography variant='titleMedium' className={classes.errorWrapper}>
                      {error.response?.data?.errors[0]?.detail ||
                        error.response?.data?.errors[0]?.title ||
                        'Sorry, there seems to be a problem on our end. Please try again later.'}
                      {error?.response?.status && (
                        <Typography
                          // @ts-expect-error FIXME TS2339
                          className={classes.errorCodeText}
                        >{`Error Code: ${error?.response?.status}`}</Typography>
                      )}
                    </Typography>
                  ),
                }),
              ),
            }),
          ),
        );
      });
    // @ts-expect-error FIXME TS2339
  }, [institutionLogin, classes.errorCodeText, classes.errorWrapper, dispatch]);
  // ====================================== Dialog Kickoffs ================================================ //
  const kickoffResetFi = useCallback(() => {
    // const { institutionId, channel } = institutionLogin;
    if (institutionLogin) {
      dispatch(
        rootUiActions.createDialog(
          rootUiTypes.mkRootUiData({
            id: 'reset-login-connection',
            type: CONFIRMATION_DIALOG_TYPE,
            allowNesting: true,
            props: ImmutableMap(
              mkConfirmationDialogProps({
                title: 'Reset connection',
                content: `Resetting your connection will disconnect ${institutionLogin.name} from Quicken and prompt you to reconnect.
                If restoring connectivity is not successful the institution will remain in Quicken but will be disconnected with the ability to connect it at a later point in time.`,
                confirmLabel: 'Reset',
                denyLabel: 'Cancel',
                confirmAction: () =>
                  actionHelpers
                    .bindPromiseAction(
                      dispatch,
                      institutionLoginsActions.qcsInstitutionLoginAction,
                    )({ qcsAction: 'disconnect', id: institutionLogin.id, params: { doSynchronous: true } })
                    .then(() => dispatch(accountDiscoveryActions.doUpdateFi({ institutionLogin }))),
              }),
            ),
          }),
        ),
      );
    }
  }, [dispatch, institutionLogin]);

  const kickoffShowManageUpdates = useCallback(() => {
    if (institutionLogin) {
      showManageUpdatesDialog({ defaultValue: institutionLogin.id });
    }
  }, [institutionLogin]);

  const kickoffCloseFi = useCallback(() => {
    if (institutionLogin) {
      dispatch(
        rootUiActions.createDialog(
          rootUiTypes.mkRootUiData({
            id: 'close-institution',
            type: CONFIRMATION_DIALOG_TYPE,
            allowNesting: true,
            props: ImmutableMap(
              mkConfirmationDialogProps({
                title: 'Close institution',
                content: `Closing ${institutionLogin?.name} will set the account balances to $0 and disable downloading of future transactions in Quicken.
                 You will still be able to access all of your old transactions`,
                confirmLabel: 'Confirm',
                denyLabel: 'Cancel',
                confirmAction: () =>
                  actionHelpers.bindPromiseAction(
                    dispatch,
                    institutionLoginsActions.qcsInstitutionLoginAction,
                  )({ qcsAction: 'close', id: institutionLogin?.id }),
              }),
            ),
          }),
        ),
      );
    }
  }, [institutionLogin, dispatch]);

  const kickoffDeleteFi = useCallback(() => {
    dispatch(
      rootUiActions.createDialog(
        rootUiTypes.mkRootUiData({
          id: 'delete-institution',
          type: CONFIRMATION_DIALOG_TYPE,
          allowNesting: true,
          props: ImmutableMap(
            mkConfirmationDialogProps({
              title: 'Delete institution',
              content: `Deleting ${institutionLogin?.name} will remove all of its data and transactions from Quicken.
               This will not fix connection issues.`,
              confirmLabel: 'Delete',
              confirmButtonVariant: 'delete',
              denyLabel: 'Cancel',
              confirmAction: () => deleteAccount(),
            }),
          ),
        }),
      ),
    );
  }, [dispatch, institutionLogin, deleteAccount]);

  const handleRequestCreate = useCallback(() => {
    dispatch(accountDiscoveryActions.doAccountRediscovery(institutionLogin, { dialogTitle: 'Add account' }));
  }, [dispatch, institutionLogin]);

  const kickoffEditFi = useCallback(() => {
    toggleShowEditDialog();
  }, [toggleShowEditDialog]);

  const kickoffDisconnectFi = useCallback(() => {
    if (institutionLogin) {
      actionHelpers.bindPromiseAction(
        dispatch,
        institutionLoginsActions.qcsInstitutionLoginAction,
      )({ qcsAction: 'disconnect', id: institutionLogin?.id });
    }
  }, [dispatch, institutionLogin]);

  const kickoffConnectFi = useCallback(() => {
    if (institutionLogin) {
      dispatch(accountDiscoveryActions.doUpdateFi({ institutionLogin }));
    }
  }, [dispatch, institutionLogin]);

  const kickoffZillowDialog = useCallback(() => {
    dispatch(showRealEstateDialog());
  }, [dispatch]);

  const kickoffKbbDialog = useCallback(() => {
    dispatch(showVehicleDialog({ dialogOrigin: VehicleDialogOrigin.AddAccount }));
  }, [dispatch]);

  // ====================================== Memoized Values ================================================ //

  const handleAddAccount = useCallback(() => {
    if (isZillow) {
      kickoffZillowDialog();
      return;
    }
    if (isKbb) {
      kickoffKbbDialog();
      return;
    }
    handleRequestCreate();
  }, [isZillow, isKbb, kickoffZillowDialog, kickoffKbbDialog, handleRequestCreate]);

  const handleFixError = useCallback(() => {
    if (!aggLoading && aggAction) {
      aggErrors.kickoffAggAction(dispatch, aggAction, institutionLogin, 'account settings');
    }
  }, [aggLoading, dispatch, aggAction, institutionLogin]);

  const handleSetupTransfers = useCallback(() => {
    if (institutionLogin?.institutionId) {
      kickoffA2AFlow({
        initiator: 'FI account details',
        startTabIndex: onboardA2ADialogIndexEnums.SELECT_ACCT,
        initialInstitutionId: institutionLogin.institutionId,
      });
    }
  }, [institutionLogin, kickoffA2AFlow]);

  const updatedInstitutionNameErrorMessage = useMemo(() => {
    if (updatedInstitutionName === null || !institutionLogin) return null;
    if (updatedInstitutionName.length === 0) return 'New name must be more than 0 characters';
    if (updatedInstitutionName === institutionLogin?.name || updatedInstitutionName === title)
      return 'New name must be different from current name';
    return null;
  }, [updatedInstitutionName, institutionLogin, title]);

  const isAppleWallet = institutionLogin?.channel === 'APPLE_WALLET';
  const showAddAccount = Boolean(
    institutionLogin?.isConnected && !isReportableError && !isAppleWallet && !connectionUpdate.institutionLogin,
  );
  const showConnect =
    !loginHasConnectedAccounts && !isZillow && !isKbb && !accounts?.every((acc) => acc.isClosed ?? false);
  const showConnectedActions = Boolean(loginHasConnectedAccounts && !isKbb && !isZillow);
  const showDisconnect = showExpandedMenu && showConnectedActions;

  return (
    <Paper id={id}>
      <Stack gap={2} padding={2}>
        {/* ============================================ Header ============================================ */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent={{ xs: 'flex-start', sm: 'space-between' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          gap={2}
        >
          <Dump obj={institutionLogin} />
          <Stack direction='row' alignItems='center' justifyContent='space-between' gap={2} width='100%'>
            <InstitutionItem value={institutionLogin?.id} title={title} id={`${id}-title`} />
            {title === 'Manual Accounts' && (
              <div className={classes.buttonContainer}>
                <Button
                  size='small'
                  variant='contained'
                  onClick={() => {
                    // onHandleCloseOverride();
                    dispatch(
                      accountDiscoveryActions.doAccountDiscovery({
                        initiator: 'manual accounts',
                        mode: 'settings accounts link',
                      }),
                    );
                  }}
                  startIcon={<LinkIcon />}
                >
                  Link Financial Institutions
                </Button>
              </div>
            )}
          </Stack>
          {institutionLogin && (
            <Stack
              direction='row'
              gap={1}
              alignItems='center'
              justifyContent={{ xs: 'space-between', sm: 'flex-end' }}
              width='100%'
            >
              <AccountRefreshChip institutionLoginId={institutionLogin.id} />
              <AccountManageUpdatesChip institutionLoginId={institutionLogin.id} />
              <span
                onClick={(event: React.MouseEvent<HTMLSpanElement>) => {
                  if (event.shiftKey) setShowExpandedMenu(true);
                }}
              >
                <QDropdownMenu
                  id={menuId}
                  aria-label='Login Menu'
                  iconButtonSize='medium'
                  onOpenChange={(open) => {
                    setIsMenuOpen(open);
                    if (!open) setShowExpandedMenu(false);
                  }}
                  width={a2aTransferEnabled ? 260 : 200}
                >
                  {isAppleWallet ? (
                    <QMenuItem
                      variant='action'
                      label='Edit institution'
                      id={`${menuId}-edit`}
                      startIcon={<EditIcon />}
                      onClick={kickoffEditFi}
                    />
                  ) : null}
                  {isAppleWallet ? <QMenuDivider /> : null}

                  {!isAppleWallet && showAddAccount ? (
                    <QMenuItem
                      variant='action'
                      label='Add a new account'
                      id={`${menuId}-add-account`}
                      startIcon={<AddCircleIcon />}
                      onClick={handleAddAccount}
                    />
                  ) : null}

                  {!isAppleWallet && isReportableError ? (
                    <QMenuItem
                      variant='action'
                      label='Fix error'
                      id={`${menuId}-fix-error`}
                      startIcon={<ErrorRoundedIcon />}
                      disabled={aggLoading}
                      onClick={handleFixError}
                    />
                  ) : null}
                  {!isAppleWallet && isReportableError ? <QMenuDivider /> : null}

                  {!isAppleWallet && institutionLogin ? (
                    <QMenuItem
                      variant='action'
                      label='Edit institution'
                      id={`${menuId}-edit`}
                      startIcon={<EditIcon />}
                      onClick={kickoffEditFi}
                    />
                  ) : null}
                  {!isAppleWallet && institutionLogin ? <QMenuDivider /> : null}

                  {!isAppleWallet && !isZillow && !isKbb && institutionLogin && a2aTransferEnabled ? (
                    <QMenuItem
                      variant='action'
                      label='Set up account transfers'
                      id={`${menuId}-setup-transfers`}
                      startIcon={<SwapHorizRoundedIcon />}
                      onClick={handleSetupTransfers}
                    />
                  ) : null}
                  {!isAppleWallet && !isZillow && !isKbb && institutionLogin && a2aTransferEnabled ? (
                    <QMenuDivider />
                  ) : null}

                  {!isAppleWallet && showConnect ? (
                    <QMenuItem
                      variant='action'
                      label='Connect'
                      id={`${menuId}-connect`}
                      startIcon={<ExploreIcon />}
                      onClick={kickoffConnectFi}
                    />
                  ) : null}

                  {!isAppleWallet && showConnectedActions ? (
                    <QMenuItem
                      variant='action'
                      label={
                        connectionUpdate.institutionLogin ? (
                          <Stack direction='row' gap={1} alignItems='center'>
                            Update connection{' '}
                            <Chip
                              size='small'
                              variant='tonal'
                              color='primary'
                              label='New'
                              sx={{ borderRadius: 'lg' }}
                            />
                          </Stack>
                        ) : (
                          'Reset connection'
                        )
                      }
                      id={`${menuId}-${connectionUpdate.institutionLogin ? 'update-connection' : 'reset'}`}
                      startIcon={
                        connectionUpdate.institutionLogin ? (
                          <ConnectionUpdateIcon size={18} tone='neutral' />
                        ) : (
                          <RefreshIcon />
                        )
                      }
                      onClick={
                        connectionUpdate.institutionLogin
                          ? () => {
                              trackMigrationPrompt('clicked');
                              connectionUpdate.onUpdate();
                            }
                          : kickoffResetFi
                      }
                    />
                  ) : null}
                  {!isAppleWallet && showConnectedActions && isRefreshFrequencySupported ? (
                    <QMenuItem
                      variant='action'
                      label='Manage updates'
                      id={`${menuId}-manage-updates`}
                      startIcon={<ManageHistoryIcon />}
                      onClick={kickoffShowManageUpdates}
                    />
                  ) : null}
                  {!isAppleWallet && showConnectedActions ? (
                    <QMenuItem
                      variant='action'
                      label='Mark as closed'
                      id={`${menuId}-close`}
                      startIcon={<NotInterestedRoundedIcon />}
                      onClick={kickoffCloseFi}
                    />
                  ) : null}
                  {!isAppleWallet && showConnectedActions ? <QMenuDivider /> : null}

                  {!isAppleWallet && showDisconnect ? (
                    <QMenuItem
                      variant='action'
                      label='Disconnect institution'
                      id={`${menuId}-disconnect`}
                      startIcon={<ExploreOffIcon />}
                      onClick={kickoffDisconnectFi}
                    />
                  ) : null}

                  <QMenuItem
                    variant='action'
                    label='Delete institution'
                    id={`${menuId}-delete`}
                    destructive
                    startIcon={<DeleteIcon />}
                    onClick={kickoffDeleteFi}
                  />
                </QDropdownMenu>
              </span>
            </Stack>
          )}
        </Stack>
        {/* ============================================ Content ============================================ */}
        {accounts && (
          <Stack gap={1}>
            {connectionUpdate.institutionLogin ? (
              <Stack gap={2}>
                {fiStatusEnabled && (
                  <InstitutionLoginStatusBanner
                    institutionLoginId={connectionUpdate.institutionLogin.id}
                    location='account settings'
                    showBoth
                    hideMigrationAction
                  />
                )}
                <AccountConnectionUpdateBanner
                  institutionLoginId={connectionUpdate.institutionLogin.id}
                  location='account settings'
                />
              </Stack>
            ) : institutionLogin?.channel === 'APPLE_WALLET' ? (
              <AppleWalletErrorBanner institutionLoginId={institutionLogin?.id} />
            ) : fiStatusEnabled && institutionLogin ? (
              <InstitutionLoginStatusBanner
                institutionLoginId={institutionLogin.id}
                className={classes.statusBanner}
                location='account settings'
                showBoth={true}
              />
            ) : (
              <InstitutionErrorBanner institutionLogin={institutionLogin} location='account settings' />
            )}
            <List sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 0.25 }}>
              {accounts.map((account, index) => (
                <React.Fragment key={account.id || account.clientId}>
                  <AccountListItem
                    account={account}
                    institutionLogin={institutionLogin}
                    index={index}
                    errorBannerShowing={isReportableError}
                  />
                  {index < accounts.size - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Stack>
        )}
      </Stack>
      {/* ========================================== Edit Dialog ========================================== */}
      <StdDialog
        onClose={toggleShowEditDialog}
        open={showEditDialog}
        showCloseButton
        size='sm'
        title='Edit institution'
      >
        <DialogContent className={classes.dialogContent}>
          <div className={classes.dialogContentInner}>
            <InstitutionIcon institution={institution} />
            <div style={{ marginLeft: 10, flexGrow: 1 }}>
              <TextField
                id='institution-name'
                label='Name'
                style={{ width: '100%' }}
                variant='filled'
                error={Boolean(updatedInstitutionNameErrorMessage)}
                defaultValue={institutionLogin?.name || title}
                onChange={(e) => setUpdatedInstitutionName(e.target.value)}
                helperText={updatedInstitutionNameErrorMessage}
              />
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button id='cancel-institution' variant='outlined' onClick={toggleShowEditDialog}>
            Cancel
          </Button>
          <Button
            id='update-institution'
            variant='contained'
            disabled={Boolean(!updatedInstitutionName || updatedInstitutionNameErrorMessage)}
            onClick={handleUpdateInstitutionName}
          >
            Update
          </Button>
        </DialogActions>
      </StdDialog>
    </Paper>
  );
};

export default AccountCard;
